import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Shield, Plus, Edit, Trash2, Copy, Users, Lock, Check, X,
  Loader2, Search, ChevronRight, Settings, Download, Upload, FileJson,
  GitCompare, Eye, Minus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = [
  { value: '#DC2626', label: 'Vermelho' },
  { value: '#EA580C', label: 'Laranja' },
  { value: '#D97706', label: 'Âmbar' },
  { value: '#059669', label: 'Verde' },
  { value: '#0891B2', label: 'Ciano' },
  { value: '#2563EB', label: 'Azul' },
  { value: '#7C3AED', label: 'Roxo' },
  { value: '#DB2777', label: 'Rosa' },
  { value: '#64748B', label: 'Cinza' },
];

const RoleManagementPage = () => {
  const { user } = useOutletContext();
  const [systemRoles, setSystemRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({ categories: [], total_permissions: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('custom');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
    color: '#2563EB'
  });
  const [duplicateName, setDuplicateName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Import state
  const [importData, setImportData] = useState(null);
  const [importOverwrite, setImportOverwrite] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  
  // Compare state
  const [compareRoles, setCompareRoles] = useState([]);
  const [compareSearchQuery, setCompareSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [systemRes, customRes, permsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/roles/system-roles`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/roles`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/roles/available-permissions`, { withCredentials: true })
      ]);
      setSystemRoles(systemRes.data);
      setCustomRoles(customRes.data);
      setAvailablePermissions(permsRes.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Erro ao carregar roles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.display_name) {
      toast.error('Nome e nome de exibição são obrigatórios');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/roles`, formData, { withCredentials: true });
      toast.success('Role criado com sucesso');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedRole) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/roles/${selectedRole.role_id}`, {
        display_name: formData.display_name,
        description: formData.description,
        permissions: formData.permissions,
        color: formData.color
      }, { withCredentials: true });
      toast.success('Role atualizado com sucesso');
      setEditDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/roles/${selectedRole.role_id}`, { withCredentials: true });
      toast.success('Role eliminado com sucesso');
      setDeleteDialogOpen(false);
      setSelectedRole(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.detail || 'Erro ao eliminar role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedRole || !duplicateName) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/roles/${selectedRole.role_id}/duplicate?new_name=${encodeURIComponent(duplicateName)}`,
        {},
        { withCredentials: true }
      );
      toast.success('Role duplicado com sucesso');
      setDuplicateDialogOpen(false);
      setDuplicateName('');
      setSelectedRole(null);
      fetchData();
    } catch (error) {
      console.error('Error duplicating role:', error);
      toast.error(error.response?.data?.detail || 'Erro ao duplicar role');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      display_name: role.display_name,
      description: role.description || '',
      permissions: role.permissions || [],
      color: role.color || '#2563EB'
    });
    setEditDialogOpen(true);
  };

  const openDuplicateDialog = (role) => {
    setSelectedRole(role);
    setDuplicateName(`${role.name}_copy`);
    setDuplicateDialogOpen(true);
  };

  const handleExportAll = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/roles/export/all`, { withCredentials: true });
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `privaxia_roles_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`${response.data.roles_count} roles exportados com sucesso`);
    } catch (error) {
      console.error('Error exporting roles:', error);
      toast.error('Erro ao exportar roles');
    }
  };

  const handleExportSingle = async (roleId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/roles/export/${roleId}`, { withCredentials: true });
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `privaxia_role_${response.data.roles[0]?.name || 'export'}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Role exportado com sucesso');
    } catch (error) {
      console.error('Error exporting role:', error);
      toast.error('Erro ao exportar role');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.roles || !Array.isArray(data.roles)) {
          toast.error('Formato de ficheiro inválido');
          return;
        }
        setImportData(data);
        setImportPreview(data.roles);
        setImportDialogOpen(true);
      } catch (err) {
        toast.error('Erro ao ler ficheiro JSON');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImport = async () => {
    if (!importData) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/roles/import`, {
        roles: importData.roles,
        overwrite_existing: importOverwrite
      }, { withCredentials: true });
      
      const { imported, updated, skipped, errors } = response.data;
      
      let message = [];
      if (imported > 0) message.push(`${imported} importados`);
      if (updated > 0) message.push(`${updated} atualizados`);
      if (skipped > 0) message.push(`${skipped} ignorados`);
      
      toast.success(`Roles: ${message.join(', ')}`);
      
      if (errors && errors.length > 0) {
        errors.forEach(err => toast.warning(`${err.role}: ${err.error}`));
      }
      
      setImportDialogOpen(false);
      setImportData(null);
      setImportPreview([]);
      setImportOverwrite(false);
      fetchData();
    } catch (error) {
      console.error('Error importing roles:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar roles');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      permissions: [],
      color: '#2563EB'
    });
    setSelectedRole(null);
  };

  // Compare functions
  const allRoles = [...systemRoles, ...customRoles];
  
  const toggleCompareRole = (role) => {
    setCompareRoles(prev => {
      const exists = prev.find(r => r.role_id === role.role_id);
      if (exists) {
        return prev.filter(r => r.role_id !== role.role_id);
      }
      if (prev.length >= 4) {
        toast.warning('Máximo de 4 roles para comparação');
        return prev;
      }
      return [...prev, role];
    });
  };

  const openCompareDialog = () => {
    setCompareRoles([]);
    setCompareSearchQuery('');
    setCompareDialogOpen(true);
  };

  const getComparisonData = () => {
    if (compareRoles.length < 2) return { categories: [], allPermissions: [] };
    
    // Get all unique permissions from selected roles
    const allPerms = new Set();
    compareRoles.forEach(role => {
      (role.permissions || []).forEach(p => allPerms.add(p));
    });
    
    // Group by category
    const categoryMap = {};
    allPerms.forEach(perm => {
      const [category, action] = perm.split(':');
      if (!categoryMap[category]) {
        categoryMap[category] = [];
      }
      categoryMap[category].push({ permission: perm, action });
    });
    
    return {
      categories: Object.entries(categoryMap).map(([name, perms]) => ({
        name,
        label: getCategoryLabel(name),
        permissions: perms.sort((a, b) => a.action.localeCompare(b.action))
      })),
      allPermissions: Array.from(allPerms)
    };
  };

  const getCategoryLabel = (category) => {
    const labels = {
      consent: 'Consentimentos', titular: 'Titulares', rtbf: 'RTBF',
      marketing: 'Marketing', group: 'Grupos', analytics: 'Analytics',
      ropa: 'ROPA', breach: 'Violações', user: 'Utilizadores',
      tenant: 'Tenants', audit: 'Auditoria', backup: 'Backups',
      settings: 'Definições', notification: 'Notificações'
    };
    return labels[category] || category;
  };

  const getPermissionStatus = (role, permission) => {
    const hasIt = (role.permissions || []).includes(permission);
    const othersHaveIt = compareRoles.filter(r => r.role_id !== role.role_id)
      .some(r => (r.permissions || []).includes(permission));
    
    if (hasIt && !othersHaveIt) return 'unique'; // Only this role has it
    if (hasIt && othersHaveIt) return 'shared'; // Multiple roles have it
    if (!hasIt && othersHaveIt) return 'missing'; // Others have it, this doesn't
    return 'none';
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const toggleCategoryPermissions = (category) => {
    const categoryPerms = category.permissions.map(p => p.value);
    const allSelected = categoryPerms.every(p => formData.permissions.includes(p));
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  const filteredCategories = availablePermissions.categories.filter(cat =>
    cat.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.permissions.some(p => p.label.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!user || !['admin', 'super_admin', 'tenant_admin'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <p className="text-red-500 font-medium">Acesso Restrito</p>
          <p className="text-slate-500 text-sm">Apenas administradores podem gerir roles</p>
        </div>
      </div>
    );
  }

  const RoleCard = ({ role, isSystem = false }) => (
    <Card className={`hover:shadow-md transition-shadow ${!role.is_active ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${role.color}20` }}
            >
              <Shield className="w-5 h-5" style={{ color: role.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-semibold">{role.display_name}</h4>
                {isSystem && <Badge variant="secondary" className="text-xs">Sistema</Badge>}
                {!role.is_active && <Badge variant="outline" className="text-xs">Inativo</Badge>}
              </div>
              <p className="text-sm text-slate-500">{role.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{role.permissions_count} permissões</Badge>
            {role.users_count !== undefined && (
              <Badge variant="secondary">
                <Users className="w-3 h-3 mr-1" />
                {role.users_count}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {(role.permissions || []).slice(0, 5).map((perm, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {perm.split(':')[0]}
              </Badge>
            ))}
            {(role.permissions || []).length > 5 && (
              <Badge variant="outline" className="text-xs">
                +{role.permissions.length - 5}
              </Badge>
            )}
          </div>
          
          <div className="flex gap-1">
            {!isSystem && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleExportSingle(role.role_id)}
                title="Exportar"
              >
                <Download className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDuplicateDialog(role)}
              title="Duplicar"
            >
              <Copy className="w-4 h-4" />
            </Button>
            {!isSystem && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(role)}
                  title="Editar"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => {
                    setSelectedRole(role);
                    setDeleteDialogOpen(true);
                  }}
                  title="Eliminar"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const PermissionsSelector = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-400" />
        <Input
          placeholder="Pesquisar permissões..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Badge variant="secondary">
          {formData.permissions.length} selecionadas
        </Badge>
      </div>
      
      <ScrollArea className="h-[300px] border rounded-lg p-2">
        {filteredCategories.map((category) => {
          const categoryPerms = category.permissions.map(p => p.value);
          const selectedCount = categoryPerms.filter(p => formData.permissions.includes(p)).length;
          const allSelected = selectedCount === categoryPerms.length;
          const someSelected = selectedCount > 0 && !allSelected;
          
          return (
            <div key={category.category} className="mb-4">
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                onClick={() => toggleCategoryPermissions(category)}
              >
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={allSelected}
                    className={someSelected ? 'opacity-50' : ''}
                  />
                  <span className="font-medium">{category.label}</span>
                </div>
                <Badge variant="outline" className="text-xs">
                  {selectedCount}/{categoryPerms.length}
                </Badge>
              </div>
              
              <div className="ml-6 mt-2 grid grid-cols-2 gap-2">
                {category.permissions.map((perm) => (
                  <div
                    key={perm.value}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${
                      formData.permissions.includes(perm.value) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => togglePermission(perm.value)}
                  >
                    <Checkbox checked={formData.permissions.includes(perm.value)} />
                    <span className="text-sm">{perm.label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </ScrollArea>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="role-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestão de Roles
          </h1>
          <p className="text-slate-600">
            Crie e gerencie roles personalizados para a sua organização
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openCompareDialog} data-testid="compare-roles-btn">
            <GitCompare className="w-4 h-4 mr-2" />
            Comparar Roles
          </Button>
          {customRoles.length > 0 && (
            <Button variant="outline" onClick={handleExportAll} data-testid="export-all-btn">
              <Download className="w-4 h-4 mr-2" />
              Exportar Todos
            </Button>
          )}
          <label>
            <input
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button variant="outline" asChild>
              <span className="cursor-pointer">
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </span>
            </Button>
          </label>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-role-btn">
            <Plus className="w-4 h-4 mr-2" />
            Criar Role
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Roles Sistema</p>
                <p className="text-2xl font-bold">{systemRoles.length}</p>
              </div>
              <Lock className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Roles Customizados</p>
                <p className="text-2xl font-bold text-[#10B981]">{customRoles.length}</p>
              </div>
              <Settings className="w-8 h-8 text-[#10B981]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Permissões Disponíveis</p>
                <p className="text-2xl font-bold text-blue-600">{availablePermissions.total_permissions}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Categorias</p>
                <p className="text-2xl font-bold text-purple-600">{availablePermissions.categories.length}</p>
              </div>
              <ChevronRight className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Roles Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="custom">
            Roles Customizados ({customRoles.length})
          </TabsTrigger>
          <TabsTrigger value="system">
            Roles do Sistema ({systemRoles.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="custom" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : customRoles.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Settings className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-medium text-slate-600 mb-2">Sem roles customizados</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Crie o seu primeiro role customizado para definir permissões específicas
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Role
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {customRoles.map((role) => (
                <RoleCard key={role.role_id} role={role} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="system" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {systemRoles.map((role) => (
              <RoleCard key={role.role_id} role={role} isSystem />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Role</DialogTitle>
            <DialogDescription>
              Defina um novo role com permissões específicas para a sua organização
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome (identificador)</Label>
                <Input
                  placeholder="ex: gestor_marketing"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Nome de Exibição</Label>
                <Input
                  placeholder="ex: Gestor de Marketing"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                placeholder="Descrição das responsabilidades deste role..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Cor</Label>
              <div className="flex gap-2 mt-2">
                {COLORS.map((color) => (
                  <button
                    key={color.value}
                    className={`w-8 h-8 rounded-full border-2 ${
                      formData.color === color.value ? 'border-slate-900' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            
            <Separator />
            
            <div>
              <Label className="mb-2 block">Permissões</Label>
              <PermissionsSelector />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Criar Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Role</DialogTitle>
            <DialogDescription>
              Atualize as permissões e detalhes do role "{selectedRole?.display_name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nome de Exibição</Label>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Cor</Label>
              <div className="flex gap-2 mt-2">
                {COLORS.map((color) => (
                  <button
                    key={color.value}
                    className={`w-8 h-8 rounded-full border-2 ${
                      formData.color === color.value ? 'border-slate-900' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            
            <Separator />
            
            <div>
              <Label className="mb-2 block">Permissões</Label>
              <PermissionsSelector />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Role</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que deseja eliminar o role "{selectedRole?.display_name}"?
              Esta ação não pode ser revertida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicar Role</DialogTitle>
            <DialogDescription>
              Criar uma cópia do role "{selectedRole?.display_name}" com um novo nome
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label>Nome do Novo Role</Label>
            <Input
              placeholder="ex: gestor_marketing_junior"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleDuplicate} disabled={submitting || !duplicateName}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="w-5 h-5" />
              Importar Roles
            </DialogTitle>
            <DialogDescription>
              Reveja os roles a importar antes de confirmar
            </DialogDescription>
          </DialogHeader>
          
          {importData && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p><strong>Exportado em:</strong> {new Date(importData.exported_at).toLocaleString('pt-PT')}</p>
                <p><strong>Por:</strong> {importData.exported_by}</p>
                <p><strong>Roles:</strong> {importData.roles_count}</p>
              </div>
              
              <div>
                <Label className="mb-2 block">Roles a importar:</Label>
                <ScrollArea className="h-[200px] border rounded-lg">
                  {importPreview.map((role, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${role.color || '#6B7280'}20` }}
                        >
                          <Shield className="w-4 h-4" style={{ color: role.color || '#6B7280' }} />
                        </div>
                        <div>
                          <p className="font-medium">{role.display_name}</p>
                          <p className="text-xs text-slate-500">{role.name}</p>
                        </div>
                      </div>
                      <Badge variant="outline">{role.permissions?.length || 0} permissões</Badge>
                    </div>
                  ))}
                </ScrollArea>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                <Checkbox 
                  id="overwrite" 
                  checked={importOverwrite}
                  onCheckedChange={setImportOverwrite}
                />
                <label htmlFor="overwrite" className="text-sm text-amber-800 cursor-pointer">
                  Sobrescrever roles existentes com o mesmo nome
                </label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setImportDialogOpen(false);
                setImportData(null);
                setImportPreview([]);
                setImportOverwrite(false);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleImport} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Importar {importPreview.length} Roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5" />
              Comparar Roles
            </DialogTitle>
            <DialogDescription>
              Selecione 2 a 4 roles para comparar as suas permissões lado a lado
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Role Selector */}
            {compareRoles.length < 2 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Pesquisar roles..."
                    value={compareSearchQuery}
                    onChange={(e) => setCompareSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                </div>
                
                <ScrollArea className="h-[200px] border rounded-lg">
                  {allRoles
                    .filter(r => 
                      r.display_name.toLowerCase().includes(compareSearchQuery.toLowerCase()) ||
                      r.name.toLowerCase().includes(compareSearchQuery.toLowerCase())
                    )
                    .map((role) => {
                      const isSelected = compareRoles.some(r => r.role_id === role.role_id);
                      return (
                        <div
                          key={role.role_id}
                          className={`flex items-center justify-between p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => toggleCompareRole(role)}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox checked={isSelected} />
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${role.color}20` }}
                            >
                              <Shield className="w-4 h-4" style={{ color: role.color }} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{role.display_name}</span>
                                {role.is_system && <Badge variant="secondary" className="text-xs">Sistema</Badge>}
                              </div>
                              <p className="text-xs text-slate-500">{role.permissions_count} permissões</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </ScrollArea>
                
                <p className="text-sm text-slate-500 text-center">
                  {compareRoles.length}/4 roles selecionados (mínimo 2)
                </p>
              </div>
            )}

            {/* Selected Roles Display */}
            {compareRoles.length >= 2 && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-500">Comparando:</span>
                  {compareRoles.map((role) => (
                    <Badge 
                      key={role.role_id}
                      className="flex items-center gap-1 cursor-pointer hover:opacity-80"
                      style={{ backgroundColor: role.color, color: 'white' }}
                      onClick={() => toggleCompareRole(role)}
                    >
                      {role.display_name}
                      <X className="w-3 h-3" />
                    </Badge>
                  ))}
                  {compareRoles.length < 4 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCompareRoles(prev => prev.slice(0, -1))}
                      className="text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Adicionar
                    </Button>
                  )}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 text-xs p-2 bg-slate-50 rounded-lg">
                  <span className="font-medium">Legenda:</span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-green-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    Tem
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-amber-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    Exclusivo
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-red-100 flex items-center justify-center">
                      <Minus className="w-3 h-3 text-red-400" />
                    </div>
                    Não tem
                  </span>
                </div>

                {/* Comparison Table */}
                <ScrollArea className="h-[400px] border rounded-lg">
                  <div className="min-w-max">
                    {/* Header */}
                    <div className="flex sticky top-0 bg-white border-b z-10">
                      <div className="w-48 p-3 font-medium text-slate-700 border-r bg-slate-50 sticky left-0">
                        Permissão
                      </div>
                      {compareRoles.map((role) => (
                        <div 
                          key={role.role_id}
                          className="flex-1 min-w-[140px] p-3 text-center font-medium border-r"
                          style={{ backgroundColor: `${role.color}10` }}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: role.color }}
                            />
                            <span className="truncate">{role.display_name}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{role.permissions_count} perms</p>
                        </div>
                      ))}
                    </div>

                    {/* Rows by Category */}
                    {getComparisonData().categories.map((category) => (
                      <div key={category.name}>
                        {/* Category Header */}
                        <div className="flex bg-slate-100 border-b">
                          <div className="w-48 p-2 font-semibold text-slate-800 border-r sticky left-0 bg-slate-100">
                            {category.label}
                          </div>
                          {compareRoles.map((role) => (
                            <div key={role.role_id} className="flex-1 min-w-[140px] border-r" />
                          ))}
                        </div>

                        {/* Permission Rows */}
                        {category.permissions.map((perm) => (
                          <div key={perm.permission} className="flex border-b hover:bg-slate-50">
                            <div className="w-48 p-2 text-sm text-slate-600 border-r sticky left-0 bg-white">
                              {perm.action.charAt(0).toUpperCase() + perm.action.slice(1)}
                            </div>
                            {compareRoles.map((role) => {
                              const status = getPermissionStatus(role, perm.permission);
                              return (
                                <div 
                                  key={role.role_id}
                                  className="flex-1 min-w-[140px] p-2 flex items-center justify-center border-r"
                                >
                                  {status === 'shared' && (
                                    <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center">
                                      <Check className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                  {status === 'unique' && (
                                    <div className="w-6 h-6 rounded bg-amber-500 flex items-center justify-center" title="Exclusivo deste role">
                                      <Check className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                  {status === 'missing' && (
                                    <div className="w-6 h-6 rounded bg-red-100 flex items-center justify-center">
                                      <Minus className="w-4 h-4 text-red-400" />
                                    </div>
                                  )}
                                  {status === 'none' && (
                                    <div className="w-6 h-6" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {compareRoles.map((role) => {
                    const permsCount = role.permissions?.length || 0;
                    const uniquePerms = (role.permissions || []).filter(p => 
                      !compareRoles.filter(r => r.role_id !== role.role_id)
                        .some(r => (r.permissions || []).includes(p))
                    ).length;
                    
                    return (
                      <div 
                        key={role.role_id}
                        className="p-3 rounded-lg border"
                        style={{ borderColor: role.color }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: role.color }}
                          />
                          <span className="font-medium text-sm truncate">{role.display_name}</span>
                        </div>
                        <div className="text-xs text-slate-500 space-y-1">
                          <p>Total: <strong>{permsCount}</strong> permissões</p>
                          <p>Exclusivas: <strong className="text-amber-600">{uniquePerms}</strong></p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setCompareDialogOpen(false);
                setCompareRoles([]);
              }}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagementPage;
